import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import { EMPTY_RICH_TEXT_DOCUMENT, } from "@diality/contracts";
import { Accordion, AccordionItem } from "../components/ui/accordion";
import { Tabs, TabsList, TabsTrigger, TabsPanel } from "../components/ui/tabs";
import { Reveal } from "../components/ui/reveal";
import { cn } from "../lib/utils";
import { RichTextDocumentView } from "./rich-text";
import { PAGE_GUTTER, Section } from "./_section";
/**
 * The answer body's own type scale, from the design prototype. Spacing is
 * padding-bottom rather than margin so it survives the collapse: a margin on
 * the last child would escape the `overflow-hidden` wrapper and leave a gap
 * under a closed entry.
 */
const ANSWER_PROSE = cn("font-body text-[16px] font-light leading-[1.55] text-pretty @min-[901px]:text-[17px]", "[&_p]:my-0 [&_p]:pb-6 [&_p]:leading-[1.55] [&_p]:last:pb-7 @min-[901px]:[&_p]:pb-5", "[&_ul]:my-0 [&_ul]:pb-6 [&_ul]:pl-6 @min-[901px]:[&_ul]:pb-7", "[&_ol]:my-0 [&_ol]:pb-6 [&_ol]:pl-6 @min-[901px]:[&_ol]:pb-7", "[&_li]:my-0 [&_li+li]:mt-2.5", 
// `bolder` on a 300-weight parent computes to 400, not bold — pin it.
"[&_strong]:font-bold [&_em]:italic", "[&_a]:font-medium [&_a]:text-brand-blue-dark [&_a]:underline [&_a]:decoration-1 [&_a]:underline-offset-[3px]");
/**
 * The disclosure itself, without block chrome — so a component harness renders
 * exactly what a page renders.
 *
 * `type="single"` is the story's rule: opening one entry closes whichever other
 * entry was open. Each item is keyed by its **position**, never by its title,
 * because two entries may carry the same title and must still open
 * independently. The id also lands in `aria-controls` as a raw CSS id selector,
 * so it stays identifier-safe by construction.
 */
export function AccordionEntriesView({ entries, }) {
    const instanceId = React.useId().replace(/[^a-zA-Z0-9_-]/g, "");
    const accordionRef = React.useRef(null);
    const [open, setOpen] = React.useState([]);
    React.useEffect(() => {
        if (open.length === 0)
            return;
        const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        const startScroll = window.scrollY;
        let startTime;
        let frame;
        const scrollToRow = (time) => {
            const trigger = accordionRef.current?.querySelector(`[id="accordion-trigger-${open[0]}"]`);
            if (!trigger)
                return;
            startTime ??= time;
            const progress = reducedMotion
                ? 1
                : Math.min((time - startTime) / 480, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            // The fixed marketing nav ends 84px below the viewport's top edge.
            const navOffset = 84 + 16;
            // Re-measure each frame as the previous row collapses above this one.
            const target = window.scrollY + trigger.getBoundingClientRect().top - navOffset;
            window.scrollTo({
                top: startScroll + (target - startScroll) * eased,
                behavior: "instant",
            });
            if (progress < 1)
                frame = requestAnimationFrame(scrollToRow);
        };
        frame = requestAnimationFrame(scrollToRow);
        return () => cancelAnimationFrame(frame);
    }, [open]);
    return (_jsx(Accordion, { ref: accordionRef, type: "single", open: open, onOpenChange: setOpen, "data-accordion-entries": true, children: entries.map((entry, index) => (_jsx(AccordionItem, { id: `${instanceId}-entry-${index + 1}`, title: entry.title, children: _jsx(RichTextDocumentView, { document: entry.body, className: ANSWER_PROSE }) }, `entry-${index + 1}`))) }));
}
function AccordionSectionsView({ sections }) {
    return (_jsx("div", { className: "flex flex-col gap-10", children: sections.map((section, index) => section.entries.length > 0 ? (_jsxs(Reveal, { delay: 0.06 + index * 0.08, eager: true, "data-accordion-section": true, children: [section.title && (_jsx("h2", { className: "type-eyebrow-lg m-0 mb-2", children: section.title })), _jsx(AccordionEntriesView, { entries: section.entries })] }, index)) : null) }));
}
export function AccordionBlock({ data }) {
    const [selectedTab, setSelectedTab] = React.useState("");
    // CMS updates can remove or replace the selected tab without remounting the
    // block. Resolve selection against the current tree so content stays visible.
    const activeTab = data.tabs?.find((tab) => tab.id === selectedTab) ?? data.tabs?.[0];
    return (_jsx(Section, { tone: "default", className: PAGE_GUTTER, children: data.tabs && data.tabs.length > 1 ? (_jsxs(Tabs, { value: activeTab?.id ?? "", onValueChange: setSelectedTab, children: [_jsx(Reveal, { delay: 0.06, className: "mb-10 overflow-x-auto", children: _jsx(TabsList, { label: "Choose accordion content", children: data.tabs.map((tab) => (_jsx(TabsTrigger, { value: tab.id, children: tab.label }, tab.id))) }) }), data.tabs.map((tab) => (_jsx(TabsPanel, { value: tab.id, children: _jsx(AccordionSectionsView, { sections: tab.sections }) }, tab.id)))] })) : (_jsx(AccordionSectionsView, { sections: data.tabs?.length === 1
                ? data.tabs[0].sections
                : data.sections?.length
                    ? data.sections
                    : [{ entries: data.entries ?? [] }] })) }));
}
export function accordionFromBlock(block, _resolve) {
    const rows = block.repeats?.entries ?? [];
    const mapEntry = (item) => ({
        title: item.scalars?.title ?? "",
        body: item.documents?.body ?? EMPTY_RICH_TEXT_DOCUMENT,
    });
    const sectionRows = block.repeats?.sections ?? [];
    const tabRows = block.repeats?.tabs ?? [];
    const sections = sectionRows.map((section, index) => ({
        title: section.scalars?.title || undefined,
        entries: rows
            .filter((entry) => {
            const sectionId = entry.scalars?.sectionId;
            const assigned = sectionRows.some((row) => row.id && row.id === sectionId);
            return assigned ? sectionId === section.id : index === 0;
        })
            .map(mapEntry),
    }));
    if (!tabRows.length) {
        return sections.length ? { sections } : { entries: rows.map(mapEntry) };
    }
    return {
        tabs: tabRows.map((tab, index) => ({
            id: tab.id ?? `tab-${index + 1}`,
            label: tab.scalars?.label ?? "",
            sections: sections.length
                ? sections.filter((_, sectionIndex) => {
                    const tabId = sectionRows[sectionIndex].scalars?.tabId;
                    const assigned = tabRows.some((row) => row.id && row.id === tabId);
                    return assigned ? tabId === tab.id : index === 0;
                })
                : index === 0
                    ? [{ entries: rows.map(mapEntry) }]
                    : [],
        })),
    };
}
export const accordionBlockEntry = {
    Component: AccordionBlock,
    fromBlock: accordionFromBlock,
};
//# sourceMappingURL=accordion.js.map